import { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown, Check, Loader2 } from 'lucide-react';
import { imageUrl } from '../../api/client';

interface DownloadButtonProps {
  imageId: string;
  /** Original-filnamn (t.ex. "DSC_8200.jpg") — används som default download-namn. */
  filename: string;
}

type Size = 'preview' | 'full';

const SIZE_LABEL: Record<Size, string> = {
  preview: 'Preview (1600px)',
  full: 'Original',
};

/**
 * Liten knapp som laddar ner bilden i vald storlek.
 *
 * Klick på huvudknappen → laddar med senast vald storlek (default: full).
 * Klick på chevron → meny med storleksval.
 *
 * Implementationen fetchar blob:en själv (inte bara <a download>) eftersom
 * vi vill kunna sätta filnamnet med size-suffix och tvinga download även när
 * webbläsaren skulle välja att visa bilden inline.
 */
export function DownloadButton({ imageId, filename }: DownloadButtonProps) {
  const [size, setSize] = useState<Size>('full');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: globalThis.MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 1800);
    return () => clearTimeout(t);
  }, [done]);

  async function download(chosenSize: Size) {
    setBusy(true);
    try {
      // Använd cookie-auth file-endpointen (same-origin) — fungerar både
      // i dev (Vite proxy) och prod (nginx på samma domän).
      const url = imageUrl(imageId, chosenSize);
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();

      // Bygg ett filnamn med size-suffix för att skilja varianter åt om man
      // laddar samma bild flera gånger. "DSC_8200.jpg" → "DSC_8200-1600.jpg"
      // eller "DSC_8200-original.jpg".
      const base = filename.replace(/\.(jpe?g)$/i, '');
      const ext = filename.match(/\.(jpe?g)$/i)?.[1] ?? 'jpg';
      const suffix = chosenSize === 'full' ? 'original' : '1600';
      const downloadName = `${base}-${suffix}.${ext}`;

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Frigör objektet — webbläsaren har redan startat downloaden.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

      setDone(true);
      setOpen(false);
    } catch {
      // Tyst fail — användaren kan se det i nätverkstabben om något är fel.
      // Återställ till idle-state.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={menuRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => download(size)}
        disabled={busy}
        title={`Ladda ner ${SIZE_LABEL[size]}`}
        className={[
          'flex items-center gap-2 pl-3 pr-2.5 py-1.5 text-[11px] min-h-[32px] rounded-l-md transition-colors border border-r-0',
          done
            ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
            : 'text-loupe-text-2 bg-loupe-bg-3 border-loupe-border-2 hover:text-loupe-text hover:border-loupe-text-3',
        ].join(' ')}
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : done ? (
          <Check className="w-3.5 h-3.5" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        <span className="whitespace-nowrap tracking-wide">
          {done ? 'Saved' : `Download ${size === 'full' ? 'original' : '1600px'}`}
        </span>
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        title="Välj storlek"
        aria-label="Välj storlek"
        className={[
          'flex items-center justify-center w-7 min-h-[32px] rounded-r-md transition-colors border',
          'text-loupe-text-2 bg-loupe-bg-3 border-loupe-border-2 hover:text-loupe-text hover:border-loupe-text-3',
        ].join(' ')}
      >
        <ChevronDown
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className={[
            'absolute right-0 top-full mt-1.5 z-[60]',
            'bg-loupe-bg-2 border border-loupe-border rounded-lg shadow-xl',
            'min-w-[200px] py-1 overflow-hidden',
          ].join(' ')}
        >
          <div className="px-3 py-2 text-[10px] tracking-[0.15em] uppercase text-loupe-text-3 border-b border-loupe-border">
            Storlek
          </div>
          {(['full', 'preview'] as Size[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setSize(s);
                download(s);
              }}
              className={[
                'w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2',
                'hover:bg-loupe-bg-3',
                size === s ? 'text-loupe-text' : 'text-loupe-text-2',
              ].join(' ')}
            >
              <span>{SIZE_LABEL[s]}</span>
              {size === s ? <Check className="w-3 h-3 text-loupe-text-3" /> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
